import numeral from 'numeral';
import { localesToCurrency } from '../../../app/data/currencies';
import { getCurrenySymbolFromCode } from './generalHelpers';
import {
  formatColumn,
  formatNumberUsingCode,
  isValidNumberFormatColor,
  matchNumberFormatCondition,
} from './parserHelpers';
import {
  DateAcceptedFormatType,
  HorizontalAlignment,
  NumberFormatType,
} from '../../types';
import moment from 'moment';
import {
  acceptedDateDayFirstFormats,
  acceptedDateMonthFirstFormats,
  getSystemDefaultDateFormat,
} from '../date/validation';

// const NUMBER_FORMAT_REGEX = /\\.|[#0,]+(\.[#0]*)?/; // g
const CONDITION_REGEX = /\[([<>]=?|=)(\d+)\]/;
const CURRENCY_REGEX = /\[\$([a-z]{2}-[A-Z]{2})\]/;
const COLOR_REGEX = /\[([a-zA-Z]+)\]/;
const DEFAULT_VALUE_REGEX = /"([^"]+)"/;

// TODO: Remove Namespace
// https://www.ablebits.com/office-addins-blog/custom-excel-number-format/
export interface FormattedValue {
  str: string | number;
  align: HorizontalAlignment;
  color: string;
}
//   #region ApplyFormat
export function getFormattedCell(
  type = NumberFormatType.Automatic,
  format = '',
  input: string | number,
  fontSize: number,
  fontFam: string,
  columnWidth: number,
  ignoreIndents = false
): Partial<FormattedValue> {
  const acceptedFormats =
    getSystemDefaultDateFormat() === DateAcceptedFormatType.MonthFirst
      ? acceptedDateMonthFirstFormats
      : acceptedDateDayFirstFormats;

  if (!format.length)
    return {
      str: input,
    };

  if (
    type === NumberFormatType.DateNTime ||
    type === NumberFormatType.Duration
  ) {
    const date = moment(input, acceptedFormats, true);
    const str = date.isValid() ? date.format(format) : input;
    return {
      str,
    };
  }

  const codes = format.split(';');

  let output: {
    str: string | number;
    color?: string;
    align?: HorizontalAlignment;
  } = {
    str: input,
  };

  //   If condition is present [=1] then do not manage positive negatives, format will be applied if condition matches
  let containsCondition = format.match(CONDITION_REGEX) !== null;

  for (let i = 0; i < codes.length; i++) {
    const code = codes[i];
    if (code) {
      let op: string | undefined = getOperation(i);
      let conditionVal: string | undefined = i === 3 ? undefined : '0';

      if ((i === 0 && !codes[1]) || containsCondition) {
        op = undefined;
        conditionVal = undefined;
      }
      const result = parseFormat(
        code,
        input,
        fontSize,
        fontFam,
        columnWidth,
        op,
        conditionVal,
        ignoreIndents
      );

      if (result) {
        output = result;
        break;
      }
    }
  }

  return output;
}

const getOperation = (index: number) => {
  if (index === 0) {
    return '>';
  } else if (index === 1) {
    return '<';
  } else if (index === 2) return '=';
};
// #endregion

// #region parse format
function parseFormat(
  pattern: string,
  input: string | number,
  fontSize: number,
  fontFam: string,
  columnWidth: number,
  op?: string,
  conditionalValue?: string,
  ignoreIndents = false
) {
  let str = '';
  let locale = '';
  let color: string | undefined;
  let formatCode = '';
  let i = 0;
  let align: HorizontalAlignment | undefined;
  //   let startPad = '';
  //   let endPad = '';
  let padCh = ' ';

  if (op && conditionalValue) {
    const isMatched = matchNumberFormatCondition(op, conditionalValue, input);
    if (!isMatched) return;
  }

  while (i < pattern.length) {
    //   for (let i = 0; i < pattern.length; i++) {
    const c = pattern.charAt(i);
    const prevChar = pattern.charAt(i - 1);
    const nextChar = pattern.charAt(i + 1);
    const part = pattern.substring(i, pattern.length);

    if (c === '@') {
      if (numeral(input).value !== null) return;
      continue;
    }

    if (c == '*') {
      // To align numbers left in a cell, type an asterisk and a space after the number code, for example: "#,###* "
      if (formatCode.length) {
        align = HorizontalAlignment.Left;
      } else {
        align = HorizontalAlignment.Right;
      }
      if (nextChar.length) {
        padCh = nextChar;
        str.replaceAll('[PAD]', '');
        str += '[PAD]';
        i += 2;
        continue;
      }
    } else if (c === '#' || c === '0' || c === '?') {
      let wholeCode = prevChar === '(' ? '(' : '';
      let questionMarkOnly = c === '?';
      for (let j = 0; j < part.length; j++) {
        const char = part.charAt(j);
        if (
          (char === ' ' &&
            wholeCode.startsWith('#') &&
            !wholeCode.includes(' ')) ||
          char === '#' ||
          char === '/' ||
          char === '.' ||
          char === ',' ||
          char === '?' ||
          char === '%' ||
          char === 'E' ||
          char === ')' ||
          char === '+' ||
          char === '0' ||
          (!isNaN(Number(char)) && wholeCode.includes('/'))
        ) {
          wholeCode += char;
          if (char !== '?') questionMarkOnly = false;
          continue;
        }
        break;
      }
      if (questionMarkOnly) {
        str += ' '.repeat(wholeCode.length);
      } else {
        formatCode = wholeCode;
        str = formatNumberUsingCode(input, wholeCode, str, locale);
      }

      i += wholeCode.length;
      continue;
    } else if (c === '_') {
      if (nextChar === '(' || nextChar === ')') {
        i += 2;
        str += ' ';
        continue;
      }
    } else if (c === '[') {
      let conditonalFormat = '[';
      for (let s = 1; s < part.length; s++) {
        const ch = part.charAt(s);
        conditonalFormat += ch;
        if (ch === ']') break;
      }
      i += conditonalFormat.length;

      const currencyCheck = conditonalFormat.match(CURRENCY_REGEX);
      if (currencyCheck) {
        locale = currencyCheck[1];
        const code = localesToCurrency[locale];
        if (code) {
          str += getCurrenySymbolFromCode(code);
        } else {
          str += '$';
        }
        continue;
      }

      const colorMatch = conditonalFormat.match(COLOR_REGEX);
      if (colorMatch && isValidNumberFormatColor(colorMatch[1].toLowerCase())) {
        color = colorMatch[1];
        continue;
      }

      const conditionMatch = conditonalFormat.match(CONDITION_REGEX);
      if (conditionMatch) {
        const conditonalOp = conditionMatch[1];
        const conditionValue = parseInt(conditionMatch[2], 10);

        if (!matchNumberFormatCondition(conditonalOp, conditionValue, input)) {
          return;
        }
        continue;
      }
    } else if (c === '$') {
      str += '$';
      locale = 'en-US';
    } else if (c === '"') {
      //   const textRegex = /"([^"]+)"/; ///\*\s*"(.*?)"/;
      const textRegexMatch = part.match(DEFAULT_VALUE_REGEX);
      if (textRegexMatch) {
        str += textRegexMatch[1];

        i += textRegexMatch[0].length;
        continue;
      }
    } else if (c == ' ') {
      str += ' ';
    }

    // str += c;

    i++;
  }

  const indexOf = str.indexOf('[PAD]');
  if (indexOf !== -1) {
    if (ignoreIndents) {
      str = str.replaceAll('[PAD]', '');
    } else {
      const [part1, part2] = str.split('[PAD]');

      str = formatColumn(columnWidth, fontSize, fontFam, part1, padCh, part2);
    }
  }

  return {
    str,
    color,
    align,
  };
}
//   #endregion
